/**
 * Leitmotif brand primitives.
 *
 * BRAND RULE (non-negotiable):
 *   1. The Leitmotif company name is NEVER set as text — only the SVG
 *      wordmark.
 *   2. Product names (Connection Designer, Permit Planner, …) are
 *      NEVER set as text — only the product SVG lockup.
 *   3. The "+" mark is the Leitmotif brand glyph — NEVER substitute
 *      a unicode "+" or compose product names from "+" + typography.
 *
 *   <BrandMark />                   the "+" mark (currentColor) — alone
 *   <Wordmark />                    "+Leitmotif" company wordmark (black)
 *   <WordmarkInverse />             white + pale-teal for dark surfaces
 *   <StackedWordmark />             "+Leitmotif / Systems" stacked
 *   <ConnectionDesignerLockup />    product lockup, stacked
 *   <ConnectionDesignerHorizontal />  product lockup, horizontal
 *
 *  Use only these components anywhere "Leitmotif" or a product name
 *  appears in the UI. If a new product SVG is needed, add it under
 *  src/assets/brand/ and expose a typed component here.
 */
import markBlackUrl from '../../assets/brand/mark-black.svg';
import wordmarkBlackUrl from '../../assets/brand/wordmark-black.svg';
import wordmarkInverseUrl from '../../assets/brand/wordmark-alt-white-teal.svg';
import stackedBlackUrl from '../../assets/brand/stacked-black.svg';
import connectionDesignerStackedUrl from '../../assets/brand/connection-designer-stacked.svg';
import connectionDesignerHorizontalUrl from '../../assets/brand/connection-designer-horizontal.svg';

export interface BrandSvgProps {
  height?: number;
  width?: number;
  className?: string;
  /** Accessible label. Empty string marks as decorative. */
  label?: string;
}

function svgImg(src: string, defaultLabel: string) {
  return function SvgImg({ height = 32, width, className, label = defaultLabel }: BrandSvgProps) {
    const isDecorative = label === '';
    return (
      <img
        src={src}
        height={height}
        width={width}
        className={className}
        alt={isDecorative ? '' : label}
        role={isDecorative ? 'presentation' : undefined}
        draggable={false}
      />
    );
  };
}

export const BrandMark = svgImg(markBlackUrl, 'Leitmotif');
export const Wordmark = svgImg(wordmarkBlackUrl, 'Leitmotif');
export const WordmarkInverse = svgImg(wordmarkInverseUrl, 'Leitmotif');
export const StackedWordmark = svgImg(stackedBlackUrl, 'Leitmotif Systems');

/* Aspect ratio from the wordmark asset's viewBox (2000 x 319). */
const WORDMARK_RATIO = 2000 / 319;

/**
 * Company wordmark tinted via `currentColor` — the same SVG asset applied
 * as a CSS mask, for subdued treatments like the "by Leitmotif" signature
 * (#293) where the black/inverse variants are too heavy.
 */
export function WordmarkTint({ height = 32, width, className, label = 'Leitmotif' }: BrandSvgProps) {
  const resolvedWidth = width ?? Math.round(height * WORDMARK_RATIO);
  const isDecorative = label === '';
  return (
    <span
      className={className}
      role={isDecorative ? 'presentation' : 'img'}
      aria-label={isDecorative ? undefined : label}
      style={{
        display: 'inline-block',
        width: resolvedWidth,
        height,
        backgroundColor: 'currentColor',
        WebkitMaskImage: `url(${wordmarkBlackUrl})`,
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskSize: 'contain',
        WebkitMaskPosition: 'center',
        maskImage: `url(${wordmarkBlackUrl})`,
        maskRepeat: 'no-repeat',
        maskSize: 'contain',
        maskPosition: 'center',
      }}
    />
  );
}

export const ConnectionDesignerLockup = svgImg(connectionDesignerStackedUrl, 'Connection Designer');
export const ConnectionDesignerHorizontal = svgImg(connectionDesignerHorizontalUrl, 'Connection Designer');
